import type { ReactNode } from "react";
import { ScrollView, Pressable, StyleSheet, Text, View } from "react-native";

import type { UiTheme } from "../../theme";

// ChipStrip — the reusable horizontal chip switcher chrome lifted out of the
// logger's RosterStrip (the "questo roster lo riutilizzeremo di sicuro"
// decision, 2026-06-04). It owns the shared parts: a section label + hint,
// an expand/collapse chevron, and the expanded-grid / collapsed-scroll
// layout. ≥44pt targets, shape-not-colour-only state — reduced-vision safe.
//
// Lifted to @jackedlogs/ui (S3, 2026-06-29) with MeasurementTrends so the web
// console gets the same trend metric picker; behaviour unchanged (theme is the
// only swap: ReturnType<typeof useTheme> → UiTheme). The mobile logger's
// RosterStrip imports it from here too.
//
// Two ways to use it:
//  - `children`: render your own rich chips (the logger does this — its
//    chips carry a state dot + set count + superset tag). Behaviour-preserving.
//  - `options` + `mode`: the built-in selectable chips. `mode: 'single'`
//    (default — exactly one active, like a switcher) or `'multi'` (toggle
//    several, e.g. the body-measurement trend metric picker). Controlled:
//    the parent owns `selectedKeys` and reacts to `onSelect(key)`.

export type ChipOption = { key: string; label: string };

type CommonProps = {
  label: string;
  hint?: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  expandA11y: string;
  collapseA11y: string;
  theme: UiTheme;
};

type ChildrenProps = CommonProps & {
  children: ReactNode;
  options?: never;
};

type OptionsProps = CommonProps & {
  options: ChipOption[];
  mode?: "single" | "multi";
  selectedKeys: string[];
  onSelect: (key: string) => void;
  children?: never;
};

type Props = ChildrenProps | OptionsProps;

export function ChipStrip(props: Props) {
  const { label, hint, expanded, onToggleExpanded, theme } = props;
  const styles = makeStyles(theme);

  const body =
    "children" in props && props.children !== undefined ? (
      props.children
    ) : "options" in props && props.options ? (
      props.options.map((opt) => {
        const active = props.selectedKeys.includes(opt.key);
        return (
          <Pressable
            key={opt.key}
            accessibilityRole="button"
            accessibilityState={{ selected: active }}
            onPress={() => props.onSelect(opt.key)}
            hitSlop={8}
            style={({ pressed }) => [
              styles.chip,
              active && styles.chipActive,
              pressed && styles.chipPressed,
            ]}
          >
            {/* ✓ prefix, not colour alone, marks the active chip
                ([[feedback_user_visual_accessibility]]). */}
            <Text
              style={[styles.chipLabel, active && styles.chipLabelActive]}
              numberOfLines={1}
            >
              {active ? "✓ " : ""}
              {opt.label}
            </Text>
          </Pressable>
        );
      })
    ) : null;

  return (
    <View style={styles.wrap}>
      <View style={styles.header}>
        {/* One line, then ellipsis. The label owns `flex: 1` and is the only
            shrinkable child, so without this a long section label or a long
            control beside it wraps the header onto two rows. */}
        <Text style={styles.label} numberOfLines={1}>
          {label}
          {hint ? (
            <Text style={styles.hint}>
              {"  "}
              {hint}
            </Text>
          ) : null}
        </Text>
        <Pressable
          accessibilityRole="button"
          accessibilityLabel={expanded ? props.collapseA11y : props.expandA11y}
          onPress={onToggleExpanded}
          hitSlop={8}
          style={({ pressed }) => [styles.chevron, pressed && styles.chevronPressed]}
        >
          <Text style={styles.chevronText}>{expanded ? "▴" : "▾"}</Text>
        </Pressable>
      </View>
      {expanded ? (
        <View style={styles.grid}>{body}</View>
      ) : (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.strip}
          keyboardShouldPersistTaps="handled"
        >
          {body}
        </ScrollView>
      )}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: ty } = theme;
  return StyleSheet.create({
    wrap: { gap: s.s1 },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      gap: s.s2,
    },
    label: {
      flex: 1,
      color: p.ink2,
      fontSize: ty.sizes.xs,
      fontWeight: "700",
      textTransform: "uppercase",
      letterSpacing: 0.4,
    },
    hint: {
      color: p.ink3,
      fontWeight: "500",
      textTransform: "none",
      letterSpacing: 0,
    },
    grid: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: s.s2,
      paddingVertical: s.s1,
    },
    strip: { gap: s.s2, paddingVertical: s.s1 },
    chevron: {
      width: 36,
      height: 36,
      alignItems: "center",
      justifyContent: "center",
      borderRadius: r.r2,
    },
    chevronPressed: { backgroundColor: p.surface2 },
    chevronText: { color: p.ink2, fontSize: ty.sizes.md, fontWeight: "700" },
    // Built-in selectable chip (options API). Rich custom chips come in via
    // `children` instead and bring their own styles.
    chip: {
      maxWidth: 220,
      minHeight: 36,
      flexDirection: "row",
      alignItems: "center",
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s1,
      backgroundColor: p.surface,
    },
    chipActive: {
      borderColor: p.brand500,
      borderWidth: 2,
      backgroundColor: p.brand100,
    },
    chipPressed: { backgroundColor: p.surface2 },
    chipLabel: {
      color: p.ink,
      fontSize: ty.sizes.sm,
      fontWeight: "600",
    },
    chipLabelActive: { color: p.brandText },
  });
}
